import {
  ToastProvider as DsToastProvider,
  ToastProviderProps,
  useToast as useDsToast,
  UseToastReturn,
} from '@lawpilots/design-system'
import React, { createContext, ReactNode, useContext, useLayoutEffect, useMemo, useRef } from 'react'

export type { UseToastReturn }

const TOAST_DURATION_MS = 15_000
const TOAST_DURATIONS = { success: TOAST_DURATION_MS, error: TOAST_DURATION_MS }

const StableToastContext = createContext<UseToastReturn | null>(null)

const StableToast = ({ children }: { children: ReactNode }) => {
  const dsToast = useDsToast()
  const latest = useRef(dsToast)
  useLayoutEffect(() => {
    latest.current = dsToast
  })
  const toast = useMemo<UseToastReturn>(
    () => ({
      success: (message) => latest.current.success(message),
      error: (message) => latest.current.error(message),
    }),
    [],
  )
  return <StableToastContext.Provider value={toast}>{children}</StableToastContext.Provider>
}

/** Wraps the DS ToastProvider so that `useToast` returns the same object for the life of the app. */
export const ToastProvider = ({ children, ...props }: ToastProviderProps) => (
  <DsToastProvider durations={TOAST_DURATIONS} {...props}>
    <StableToast>{children}</StableToast>
  </DsToastProvider>
)

export const useToast = (): UseToastReturn => {
  const toast = useContext(StableToastContext)
  if (!toast) throw new Error('useToast must be used inside a ToastProvider')
  return toast
}
